import React, { useRef, useState, useEffect } from "react";
import {
  Routes,
  Route,
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom"; // Import routing components
import "./App.css";
import Hero from "./Hero";
import Testimonials from "./Testimonials"; // Import Testimonials component
import StorySection from "./StorySection"; // Import StorySection component
import ColoredSection from "./ColoredSection"; // Import ColoredSection component
import GridSection from "./GridSection"; // Import GridSection component
import NewSection from "./NewSection"; // New pinned showcase section
import Footer from "./Footer"; // Import Footer component
import RequestServiceForm from "./RequestServiceForm"; // Import RequestServiceForm component
import ServiceDropdown from "./ServiceDropdown"; // Import ServiceDropdown component
import About from "./About"; // Import About component
import BlogList from "./BlogList"; // Import Blog listing page
import BlogPost from "./BlogPost"; // Import Blog post page
import "./ServiceDropdown.css"; // Import ServiceDropdown styles
import MobileServices from "./MobileServices";

function App() {
  const testimonialsRef = useRef(null); // Ref for the Testimonials component
  const formRef = useRef(null); // Ref for the RequestServiceForm component
  const storySectionRef = useRef(null);
  const gridSectionRef = useRef(null);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [isWhiteSection, setIsWhiteSection] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [hasAccess, setHasAccess] = useState(true);
  const dropdownTimeoutRef = useRef(null);
  const checkUnderNavbarRef = useRef(null);

  const location = useLocation(); // Get current location
  const navigate = useNavigate(); // Get navigate function

  // Access gating disabled; site is always available
  useEffect(() => {
    setHasAccess(true);
  }, []);

  useEffect(() => {
    const navbarHeight = 60;

    const isNearWhite = (rgbString) => {
      if (!rgbString) return false;
      const m = rgbString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
      if (!m) return false;
      const r = parseInt(m[1], 10);
      const g = parseInt(m[2], 10);
      const b = parseInt(m[3], 10);
      return r > 240 && g > 240 && b > 240; // treat very light as white
    };

    const hasWhiteBackground = (el) => {
      let node = el;
      while (node && node !== document.documentElement) {
        const cs = window.getComputedStyle(node);
        const bg = cs.backgroundColor;
        if (bg && bg !== "rgba(0, 0, 0, 0)" && bg !== "transparent") {
          return isNearWhite(bg);
        }
        node = node.parentElement;
      }
      return false;
    };

    const whiteSelectors = [
      ".story-section",
      ".grid-section",
      ".qualifiers-section",
      ".request-service-form-section",
      ".about-image-column",
      ".about-section",
    ].join(", ");

    const checkUnderNavbar = () => {
      const navbarEl = document.querySelector(".navbar");
      const btnContainer = document.querySelector(".book-now-container");
      const bannerEl = document.querySelector(".phone-number-banner");
      const x = btnContainer
        ? Math.round(
            btnContainer.getBoundingClientRect().left +
              btnContainer.getBoundingClientRect().width / 2
          )
        : Math.round(window.innerWidth / 2);
      const y = navbarHeight + 1; // just below navbar

      // Temporarily disable pointer events on overlapping navbar elements
      const targets = [navbarEl, btnContainer, bannerEl];
      const prev = targets.map((el) =>
        el ? el.style.pointerEvents : undefined
      );
      targets.forEach((el) => {
        if (el) el.style.pointerEvents = "none";
      });
      const el = document.elementFromPoint(x, y);
      targets.forEach((el, i) => {
        if (el && prev[i] !== undefined) el.style.pointerEvents = prev[i];
      });

      if (!el) {
        setIsWhiteSection(false);
        return;
      }
      const closestWhite = el.closest(whiteSelectors);
      if (closestWhite) {
        setIsWhiteSection(true);
        return;
      }
      setIsWhiteSection(hasWhiteBackground(el));
    };

    checkUnderNavbarRef.current = checkUnderNavbar;

    // Initial and on-scroll/resize updates
    checkUnderNavbar();
    window.addEventListener("scroll", checkUnderNavbar, { passive: true });
    window.addEventListener("resize", checkUnderNavbar);
    return () => {
      window.removeEventListener("scroll", checkUnderNavbar);
      window.removeEventListener("resize", checkUnderNavbar);
    };
  }, []);

  const handleMouseEnter = () => {
    clearTimeout(dropdownTimeoutRef.current);
    setShowServiceDropdown(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowServiceDropdown(false);
    }, 300); // 300ms delay
  };

  const toggleMobileMenu = () => {
    if (mobileMenuOpen) {
      // start closing animation then unmount (allow stagger to finish first)
      setMobileMenuOpen(false);
      setTimeout(() => {
        setMobileMenuVisible(false);
        setMobileServicesOpen(false);
      }, 560);
    } else {
      setMobileMenuVisible(true);
      // ensure open class applies next frame for transitions
      setTimeout(() => setMobileMenuOpen(true), 0);
    }
  };

  const handleMobileBookNow = () => {
    // close menu with animation, then navigate/scroll to form
    setMobileMenuOpen(false);
    setTimeout(() => setMobileMenuVisible(false), 560);
    handleBookNowClick();
  };

  // Close mobile menu on route change
  useEffect(() => {
    if (mobileMenuOpen || mobileMenuVisible) {
      setMobileMenuOpen(false);
      setTimeout(() => setMobileMenuVisible(false), 560);
    }
  }, [location.pathname]);

  const handleLearnMoreClick = () => {
    if (testimonialsRef.current) {
      // Scroll to Testimonials
      const navbarHeight = 60; // Height of the fixed navbar
      const elementPosition =
        testimonialsRef.current.getBoundingClientRect().top +
        window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handlePhoneHoverEnter = () => {
    setShowPhoneNumber(true);
    if (checkUnderNavbarRef.current) {
      checkUnderNavbarRef.current();
    }
  };

  const handlePhoneHoverLeave = () => {
    setShowPhoneNumber(false);
  };

  const handleBookNowClick = () => {
    if (location.pathname !== "/") {
      // Navigate home and signal to scroll to form
      navigate("/?scroll=form");
      return;
    }
    if (formRef.current) {
      const navbarHeight = 60; // Height of the fixed navbar
      const elementPosition =
        formRef.current.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      // If on home page, scroll to top
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    } else {
      // If on any other page, navigate to home page
      navigate("/");
      // Add a small delay to ensure navigation completes before scrolling
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 0); // A delay of 0ms often works to push the scroll to the next event loop cycle
    }
  };

  // If we land on home with a scroll signal, scroll to the form section
  useEffect(() => {
    if (location.pathname !== "/") return;
    const params = new URLSearchParams(location.search || "");
    const shouldScroll =
      params.get("scroll") === "form" || location.hash === "#form";
    if (!shouldScroll) return;
    // Defer to after paint so ref is available
    const t = setTimeout(() => {
      if (formRef.current) {
        const navbarHeight = 60;
        const y =
          formRef.current.getBoundingClientRect().top +
          window.pageYOffset -
          navbarHeight;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }, 0);
    return () => clearTimeout(t);
  }, [location.pathname, location.search, location.hash]);

  // Page title handling for non-blog routes
  useEffect(() => {
    if (location.pathname === "/") {
      document.title = "Megavolt Electric";
    } else if (location.pathname === "/about") {
      document.title = "Megavolt Electric | About";
    }
    // Blog routes are handled within their own components to include
    // page numbers and post titles.
  }, [location.pathname]);

  // Ensure About page always starts at top when navigated to
  useEffect(() => {
    if (location.pathname === "/about") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [location.pathname]);

  // Access gating removed; render app normally

  return (
    <div className="App">
      <nav className="navbar">
        <picture onClick={handleLogoClick} style={{ cursor: "pointer" }}>
          {/* Use the compact mobile logo only on phones; tablets use the horizontal logo below */}
          <source
            media="(max-width: 767px)"
            srcSet="/01_MVE_Main_Logo_Website-Mobile.png"
          />
          <img
            src="/02_MVE_horizontal_logo_No_01_w500.png"
            alt="MegaVolt Electric Logo"
            className="navbar-logo"
          />
        </picture>
        <div className="nav-items">
          <div className="book-now-container desktop-only">
            <button
              className="book-now-nav-button"
              onClick={handleBookNowClick}
              onMouseEnter={handlePhoneHoverEnter}
              onMouseLeave={handlePhoneHoverLeave}
            >
              Book Now
            </button>
            <div
              className={`phone-number-banner ${
                showPhoneNumber ? "show" : ""
              } ${isWhiteSection ? "white-bg" : ""}`}
            >
              (403) 369-7980
            </div>
          </div>
          <div className="nav-dropdown-wrapper desktop-only">
            <ul className="nav-menu">
              <li
                className="service-menu-item-container"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                <span className="service-menu-item">Services</span>
                {showServiceDropdown && (
                  <ServiceDropdown show={showServiceDropdown} />
                )}
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              <li>
                <Link
                  to="/about"
                  onClick={() => {
                    if (location.pathname === "/about") {
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }
                  }}
                >
                  About
                </Link>
              </li>{" "}
              {/* Link to About page */}
            </ul>
          </div>
          <button
            className={`burger mobile-only ${mobileMenuOpen ? "open" : ""}`}
            aria-label="Toggle menu"
            onClick={toggleMobileMenu}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>
      {mobileMenuVisible && (
        <div className="mobile-menu-overlay" onClick={toggleMobileMenu}>
          <div
            className={`mobile-menu ${mobileMenuOpen ? "open" : "closing"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <ul className="mobile-menu-list">
              <li className="services-li">
                <button
                  className={`mobile-menu-item mobile-services-toggle ${
                    mobileServicesOpen ? "open" : ""
                  }`}
                  onClick={() => setMobileServicesOpen((o) => !o)}
                  aria-expanded={mobileServicesOpen}
                  aria-controls="mobile-services-panel"
                >
                  <span>Services</span>
                </button>
                {mobileServicesOpen && (
                  <div id="mobile-services-panel">
                    <MobileServices />
                  </div>
                )}
              </li>
              <li>
                <Link to="/blog">Blog</Link>
              </li>
              <li>
                <Link to="/about">About</Link>
              </li>
            </ul>
            <div className="mobile-cta">
              <a className="call-button" href="tel:+14033697980">
                Give us a call
              </a>
              <button
                className="book-now-nav-button"
                onClick={handleMobileBookNow}
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      )}
      <Routes>
        {" "}
        {/* Define routes */}
        <Route
          path="/"
          element={
            <>
              <Hero onLearnMoreClick={handleLearnMoreClick} />
              <Testimonials ref={testimonialsRef} />
              <StorySection ref={storySectionRef} />
              <ColoredSection onBookNowClick={handleBookNowClick} />
              <GridSection ref={gridSectionRef} />
              <NewSection onBookNowClick={handleBookNowClick} />
              <RequestServiceForm ref={formRef} showCloseButton={false} />
              <Footer />
            </>
          }
        />
        <Route path="/about" element={<About formRef={formRef} />} />{" "}
        {/* About page route */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:id" element={<BlogPost />} />
      </Routes>
    </div>
  );
}

export default App;
